import { Activity } from "lucide-react";
import InfoBanner from "../ui/InfoBanner";
import SecretInput from "../ui/SecretInput";

// Keys whose values must never render in cleartext.
const SECRETISH = /(secret|password|passwd|token|key|credential)/i;

// "client_id" -> "Client ID", "username" -> "Username"
const ACRONYMS = new Set(["id", "url", "uri", "api", "ssn", "ein"]);
const humanize = (key = "") =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(" ");

/**
 * OAuth credentials form.
 *
 * The fields are whatever the provider API declares in `credential_fields`, not a
 * fixed Client ID + Client Secret pair. Experian's token endpoint uses a password
 * grant and needs four credentials; a two-field form could not express it, and
 * saving through it used to destroy the two it didn't know about.
 *
 * Deliberately omits the Redirect URI and "Authorize with …" button that used to
 * live here: Orbit implements token-endpoint grants only, has no
 * authorization-code callback route, and the URI shown
 * (https://orbit.nativeorange.com/oauth/<svc>/callback) pointed at a host that
 * does not resolve. Scopes are configured per endpoint, not per integration, so
 * that field is gone too — it was collected and silently discarded.
 */
export default function OAuthConnection({
  credentialKeys = [],
  credentials = {},
  onCredentialChange,
  isEditing
}) {
  return (
    <>
      <InfoBanner
        icon={Activity}
        title="OAuth 2.0 Authentication"
        description={
          isEditing
            ? "Leave a field blank to keep the credential already stored. Anything you fill in replaces just that credential."
            : "Orbit exchanges these credentials for a short-lived access token and refreshes it automatically."
        }
      />

      <div className="grid grid-cols-2 gap-4">
        {credentialKeys.map((key) => {
          const label = humanize(key);
          const value = credentials[key] || "";

          return SECRETISH.test(key) ? (
            <SecretInput
              key={key}
              label={label}
              value={value}
              onChange={(v) => onCredentialChange(key, v)}
              placeholder={isEditing ? "Leave blank to keep" : `Enter ${label}`}
              required={!isEditing}
            />
          ) : (
            <div key={key}>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {label}
                {!isEditing && <span className="text-red-500"> *</span>}
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => onCredentialChange(key, e.target.value)}
                placeholder={
                  isEditing ? "Leave blank to keep" : `Enter ${label}`
                }
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-500">
        {credentialKeys.length} credential
        {credentialKeys.length === 1 ? "" : "s"} required by this API. Stored
        encrypted, never returned to the browser.
      </p>
    </>
  );
}
